import { Link } from "react-router-dom";
import { Mail, MapPin, Phone } from "lucide-react";
import { useLang } from "@/i18n";
import { ADDRESS, EMAIL, PHONE_DISPLAY, waLink } from "@/data/site";
import { Logo } from "./Logo";

const WhatsAppIcon = ({ size = 18 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
    <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.26-.46-2.4-1.47-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.61.13-.13.3-.35.44-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.5 0 1.47 1.07 2.9 1.22 3.1.15.2 2.11 3.22 5.1 4.51.71.31 1.27.49 1.7.63.72.23 1.37.2 1.88.12.58-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35M12.05 21.79h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.85 9.85 0 0 1-1.51-5.26c0-5.45 4.44-9.88 9.9-9.88a9.83 9.83 0 0 1 9.88 9.89c0 5.45-4.44 9.88-9.89 9.88m8.42-18.3A11.82 11.82 0 0 0 12.05 0C5.5 0 .16 5.34.16 11.9c0 2.1.55 4.14 1.59 5.94L.06 24l6.3-1.65a11.88 11.88 0 0 0 5.68 1.45h.01c6.55 0 11.9-5.34 11.9-11.9 0-3.18-1.24-6.16-3.48-8.41"/>
  </svg>
);

export const Footer = () => {
  const { t } = useLang();
  const cols = [
    ["/la-casa", t.nav.casa], ["/camere", t.nav.camere], ["/gallery", t.nav.gallery],
    ["/dove-siamo", t.nav.dove], ["/cosa-vedere", t.nav.cosa], ["/esperienze", t.nav.esperienze],
    ["/recensioni", t.nav.recensioni], ["/contatti", t.nav.contatti], ["/prenota", t.nav.prenota],
  ];
  return (
    <footer data-testid="site-footer" className="relative bg-mare text-sand grain">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <p className="font-script text-4xl text-ochre sm:text-5xl">{t.footer.tagline}</p>
        <div className="mt-10 grid gap-12 md:grid-cols-3">
          <div>
            <span className="inline-block"><Logo light /></span>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-sand/70">
              {t.hero.eyebrow}
            </p>
          </div>
          <div>
            <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-ochre">{t.footer.explore}</h3>
            <ul className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2">
              {cols.map(([to, label]) => (
                <li key={to}>
                  <Link to={to} data-testid={`footer-link-${to.replace("/", "") || "home"}`} className="text-sm text-sand/80 transition-colors hover:text-ochre">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-ochre">{t.footer.contacts}</h3>
            <ul className="mt-4 space-y-3 text-sm text-sand/80">
              <li>
                <a data-testid="footer-whatsapp" href={waLink(t.form.waMsg)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 transition-colors hover:text-ochre">
                  <WhatsAppIcon /> WhatsApp
                </a>
              </li>
              <li>
                <a data-testid="footer-phone" href="tel:+393386264991" className="flex items-center gap-2.5 transition-colors hover:text-ochre">
                  <Phone size={16} /> {PHONE_DISPLAY}
                </a>
              </li>
              <li>
                <a data-testid="footer-email" href={`mailto:${EMAIL}`} className="flex items-center gap-2.5 break-all transition-colors hover:text-ochre">
                  <Mail size={16} className="shrink-0" /> {EMAIL}
                </a>
              </li>
              <li className="flex items-center gap-2.5">
                <MapPin size={16} className="shrink-0" /> {ADDRESS}
              </li>
            </ul>
          </div>
        </div>
        <div className="mt-14 flex flex-col items-start justify-between gap-3 border-t border-sand/15 pt-6 text-xs text-sand/50 sm:flex-row sm:items-center">
          <span>© {new Date().getFullYear()} Casa Vacanze Margherita — {t.footer.rights}</span>
          <span className="font-semibold tracking-[0.25em] text-ochre/80">{t.footer.payoff.toUpperCase()}</span>
        </div>
      </div>
    </footer>
  );
};

export { WhatsAppIcon };
